import type { ReactiveMap } from "@solid-primitives/map"
import { createEffect, DEV, onCleanup, type Accessor } from "solid-js"
import { ObjectSpaceNormalMap, WebGLRenderTarget, type MeshLambertMaterial, type Texture } from "three"
import { onCleanupDispose } from "../../../../../../rx/cleanup/onCleanupDispose"
import type { NeighborElevationCollection } from "../../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import { setDebugImageList } from "../../../../../../rx/signal/useSharedDebugImageListSignal"
import type { Nullable } from "../../../../../../typescript"
import removeItem from "../../../../../array/remove_item"
import type { Engine } from "../../../../../core/engine/Engine.type"
import getEightTilePositionNeighborsL1EntityTypeIdList from "../../../../../core/engine/TileGraphNode/getEightTilePositionNeighborsL1EntityTypeIdList"
import getEightTilePositionNeighborsL1IdList from "../../../../../core/engine/TileGraphNode/getEightTilePositionNeighborsL1IdList"
import getEightTilePositionNeighborsList from "../../../../../core/engine/TileGraphNode/getEightTilePositionNeighborsList"
import type { TileGraphNode } from "../../../../../core/engine/TileGraphNode/TileGraphNode.type"
import toEntityId9GLSL from "../../../../../core/engine/TileGraphNode/toEntityId9GLSL"
import toEntityTypeId9GLSL from "../../../../../core/engine/TileGraphNode/toEntityTypeId9GLSL"
import toElevation9GLSL from "../../../../../core/entity/EntityElevation/toElevation9GLSL"
import type { EntityId } from "../../../../../core/entity/EntityId.type"
import { samePosition } from "../../../../../core/tile_position_xy/samePosition"
import createDebugRenderTargetGetOffscreenCanvasFunction from "../../../../../image/createDebugRenderTargetGetOffscreenCanvasFunction"
import useEngineTextureWriter from "../../../../fn/useEngineTextureWriter"
import createNodeNeighborBaseColorCollection from "../../../TerrainMesh1V2/createNodeNeighborBaseColorCollection"
import createTileFloorShaderMaterial from "./Shader/createTileFloorShaderMaterial"
import { TextureSizeSettingId } from "./TextureSizeSettingId.enum"

const SHOW_DEBUG_IMAGES = false

// placeholder signal accessor
function getTextureSizeSetting() {
  // return TextureSizeSettingId.SM
  return TextureSizeSettingId.MD
}

export default function addTileFloorTextureEffectV2(
  engine: Engine,
  node: TileGraphNode,
  material: MeshLambertMaterial,
  neighborBaseElevationReactiveMap: ReactiveMap<number, NeighborElevationCollection>,
  materialList: Array<MeshLambertMaterial>,
  dirtyMaterialList: () => void,
): void {
  const [getTextureWriter] = useEngineTextureWriter(engine)
  const textureWriter = getTextureWriter()

  // Create WebGLRenderTarget with count=3 for multiple render targets (diffuse, normal, and AO outputs)
  const textureSize = getTextureSizeSetting()
  const renderTarget = new WebGLRenderTarget(textureSize, textureSize, { count: 3 })
  onCleanupDispose(renderTarget)
  
  const shaderMaterial = createTileFloorShaderMaterial()
  const { u_time, u_twx, u_twy, u_elevations, u_raw_elevations, u_etype_list, u_colors, u_seed_list } = shaderMaterial.uniforms

  const { x, y } = node
  const getSeed : Accessor<Nullable<EntityId>> = node.ents.l1.id
  const getEntityTypeId = node.ents.l1.etype_id
  const getEntityId = node.ents.l1.id

  createEffect((): void => {
    // const textureSize = getTextureSizeSetting()
    const seed = getSeed() || 0
    const entityTypeId = getEntityTypeId()
    if (entityTypeId) {
      const baseElevationsCollection = neighborBaseElevationReactiveMap.get(node.index)
      if (baseElevationsCollection) {
        const eightNeighborEntityTypeIdList = getEightTilePositionNeighborsL1EntityTypeIdList(node)

        u_time.value = seed
        u_twx.value = x
        u_twy.value = -y
        u_elevations.value = toElevation9GLSL(baseElevationsCollection.avgs, baseElevationsCollection.base)

        const rawElevationArray = u_raw_elevations.value as Float32Array
        let rawElevationArrayIndex = 0
        const writeRawElevation = (value: number) => {
          rawElevationArray[rawElevationArrayIndex++] = value
        }

        for (let i = 0; i < baseElevationsCollection.list.length; i++) {
          writeRawElevation(baseElevationsCollection.list[i])
        }

        writeRawElevation(baseElevationsCollection.base)
        u_raw_elevations.value = rawElevationArray
        u_etype_list.value = toEntityTypeId9GLSL(eightNeighborEntityTypeIdList, entityTypeId)
        const neighborEntityIdList = getEightTilePositionNeighborsL1IdList(node)
        u_seed_list.value = toEntityId9GLSL(neighborEntityIdList, getEntityId())

        const eightNeighbors = getEightTilePositionNeighborsList(node)
        const neighborColorCollection = createNodeNeighborBaseColorCollection(node, eightNeighbors)
        const colorArray = u_colors.value as Float32Array

        let colorArrayIndex = 0
        const writeColor = (color: { r: number; g: number; b: number }) => {
          colorArray[colorArrayIndex++] = color.r
          colorArray[colorArrayIndex++] = color.g
          colorArray[colorArrayIndex++] = color.b
        }

        for (let i = 0; i < neighborColorCollection.list.length; i++) {
          writeColor(neighborColorCollection.list[i])
        }

        writeColor(neighborColorCollection.base)

        u_colors.value = colorArray

        // await // nextTick
        textureWriter.render(shaderMaterial, renderTarget)

        // Access individual textures from the multiple render targets
        // When count > 1, textures is an array (type assertion needed for TypeScript)
        const textures : Array<Texture> = renderTarget.textures
        // const textures = (renderTarget as any).textures as Texture[]
        const diffuseTexture = textures[0]
        const normalTexture = textures[1]
        const aoTexture = textures[2]

        if (DEV) {
          // Note: Debug function may not work correctly with MRT, but we'll keep it for now
          diffuseTexture.userData.getPixels = createDebugRenderTargetGetOffscreenCanvasFunction(engine, renderTarget, diffuseTexture)
          normalTexture.userData.getPixels = createDebugRenderTargetGetOffscreenCanvasFunction(engine, renderTarget, normalTexture)
          aoTexture.userData.getPixels = createDebugRenderTargetGetOffscreenCanvasFunction(engine, renderTarget, aoTexture)
        }

        // Set up diffuse texture
        diffuseTexture.userData.node = node
        material.map = diffuseTexture
        
        // Set up normal map
        normalTexture.userData.node = node
        material.normalMap = normalTexture
        material.normalMapType = ObjectSpaceNormalMap

        // Set up AO map
        aoTexture.userData.node = node
        material.aoMap = aoTexture

        material.map.userData.node = node
        material.normalMap.userData.node = node
        material.aoMap.userData.node = node
        material.userData.node = node
        
        material.needsUpdate = true
        
        if (!materialList.includes(material)) {
          materialList.push(material)
        }
        dirtyMaterialList()

        if (SHOW_DEBUG_IMAGES) {
          if (samePosition(node, {x: 6, y: 18})) {
            // Set names on the textures for reference
            diffuseTexture.name = 'diffuseMap';
            normalTexture.name = 'normalMap';
            aoTexture.name = 'aoMap';

            // Create debug images for each texture from the MRT
            const debugImages = [
              {
                img: createDebugRenderTargetGetOffscreenCanvasFunction(engine, renderTarget, diffuseTexture)(),
                name: 'diffuseMap'
              },
              {
                img: createDebugRenderTargetGetOffscreenCanvasFunction(engine, renderTarget, normalTexture)(),
                name: 'normalMap'
              },
              {
                img: createDebugRenderTargetGetOffscreenCanvasFunction(engine, renderTarget, aoTexture)(),
                name: 'aoMap'
              }
            ];

            setDebugImageList(debugImages);
          }
        }
      }
    }
  })

  onCleanup(() => {
    removeItem(materialList, material)
    dirtyMaterialList()
  })
}
